import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from "@mui/material";

/* =========================
    CONFIGURACIÓN
========================== */
// Se crea una sola vez. Las credenciales vienen del archivo .env.
const api = axios.create({
  baseURL:
    process.env.REACT_APP_SUA_API_INTERNA_URL ||
    "https://suadesarrollo.azurewebsites.net/api",
  auth: {
    username: process.env.REACT_APP_SUA_USUARIO,
    password: process.env.REACT_APP_SUA_CLAVE,
  },
});

/** Título de la sección en ObtenerActividades (se compara sin espacios extra ni mayúsculas). */
const SECCION = "3. RECONOCIMIENTO INSTITUCIONAL";

const normalizar = (texto) =>
  String(texto ?? "")
    .trim()
    .toUpperCase();

const mensajeDeError = (error) =>
  error?.response?.data?.message ||
  error?.response?.data?.mensaje ||
  (typeof error?.response?.data === "string" ? error.response.data : null) ||
  error?.message ||
  "Error desconocido";

/** Opción de lista que permite texto en varias líneas. */
const estiloOpcion = {
  whiteSpace: "normal",
  lineHeight: 1.4,
  padding: "10px 14px",
  borderBottom: "1px solid #eaecf4",
  alignItems: "flex-start",
};

/* =========================
    COMPONENTE
========================== */
function ReconocimientoNC({
  idSolicitud,
  tipoSolicitudId,
  seccion = SECCION,
  titulo = "3. RECONOCIMIENTO INSTITUCIONAL",
}) {
  const [evaluadores, setEvaluadores] = useState([]);
  const [actividades, setActividades] = useState([]);
  const [cargandoEvaluadores, setCargandoEvaluadores] = useState(false);
  const [cargandoActividades, setCargandoActividades] = useState(false);

  const [actividad, setActividad] = useState(null);
  const [evaluador, setEvaluador] = useState(null);
  const [guardando, setGuardando] = useState(false);

  /* =========================
      EVALUADORES
  ========================== */
  useEffect(() => {
    if (!idSolicitud) return undefined;

    const controller = new AbortController();
    setCargandoEvaluadores(true);

    api
      .get("/QEquipoUsuario", {
        params: { SolicitudId: idSolicitud },
        signal: controller.signal,
      })
      .then(({ data }) =>
        setEvaluadores((data || []).filter((e) => e.evaluadorId)),
      )
      .catch((err) => {
        if (!axios.isCancel(err)) console.error("Error evaluadores", err);
      })
      .finally(() => setCargandoEvaluadores(false));

    return () => controller.abort();
  }, [idSolicitud]);

  /* =========================
      ACTIVIDADES
  ========================== */
  useEffect(() => {
    if (!tipoSolicitudId) return undefined;

    const controller = new AbortController();
    setCargandoActividades(true);

    api
      .get(`/ObtenerActividades/${tipoSolicitudId}`, {
        signal: controller.signal,
      })
      .then(({ data }) => setActividades(data || []))
      .catch((err) => {
        if (!axios.isCancel(err)) console.error("Error actividades", err);
      })
      .finally(() => setCargandoActividades(false));

    return () => controller.abort();
  }, [tipoSolicitudId]);

  /** Solo las actividades de esta sección. */
  const actividadesSeccion = useMemo(
    () =>
      actividades.filter((a) => normalizar(a.titulo) === normalizar(seccion)),
    [actividades, seccion],
  );

  /* =========================
      GUARDAR
  ========================== */
  const guardar = async () => {
    if (!actividad || !evaluador) {
      Swal.fire(
        "Atención",
        "Seleccione la actividad y el evaluador.",
        "warning",
      );
      return;
    }

    setGuardando(true);

    try {
      const { data } = await api.post("/CreaTarea", {
        // Se envían como texto, igual que antes (valor del <select>).
        evaluadorId: String(evaluador.evaluadorId),
        tarea: String(actividad.id),
        solicitudId: idSolicitud,
        grupo: "1",
        estado: "En proceso",
        comentarios: "",
        aplica: 1,
      });

      console.log("CreaTarea:", data);
      await Swal.fire("Éxito", "Tarea asignada con éxito.", "success");

      setActividad(null);
      setEvaluador(null);
    } catch (error) {
      console.error("Error al crear la tarea", error);
      Swal.fire(
        "Error",
        `No se pudo crear la tarea: ${mensajeDeError(error)}`,
        "error",
      );
    } finally {
      setGuardando(false);
    }
  };

  /* =========================
      RENDER
  ========================== */
  return (
    <div className="container-fluid">
      <div className="card shadow mb-4">
        <div className="card-header py-3">
          <h6 className="m-0 font-weight-bold text-primary">{titulo}</h6>
        </div>

        <div className="card-body">
          {/* ACTIVIDAD */}
          <Autocomplete
            options={actividadesSeccion}
            value={actividad}
            onChange={(_, valor) => setActividad(valor)}
            getOptionLabel={(o) => o?.actividad ?? ""}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            loading={cargandoActividades}
            disabled={guardando}
            noOptionsText="No hay actividades para esta sección"
            loadingText="Cargando actividades…"
            ListboxProps={{ style: { maxHeight: 380 } }}
            renderOption={(props, option) => {
              const { key, ...resto } = props;
              return (
                <li key={option.id} {...resto} style={estiloOpcion}>
                  {option.actividad}
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={`Actividad (${actividadesSeccion.length})`}
                placeholder="Escriba para buscar…"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {cargandoActividades && <CircularProgress size={18} />}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          {/* DESCRIPCIÓN COMPLETA */}
          {actividad && (
            <Paper
              variant="outlined"
              sx={{
                mt: 2,
                p: 2,
                bgcolor: "#f8f9fc",
                borderLeft: "4px solid #4e73df",
                maxHeight: 220,
                overflowY: "auto",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Actividad seleccionada
              </Typography>
              <Typography
                variant="body2"
                sx={{ mt: 0.5, whiteSpace: "pre-line", textAlign: "left" }}
              >
                {actividad.actividad}
              </Typography>
            </Paper>
          )}

          {/* EVALUADOR + GUARDAR */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              mt: 2,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <Autocomplete
              sx={{ flex: "1 1 280px" }}
              options={evaluadores}
              value={evaluador}
              onChange={(_, valor) => setEvaluador(valor)}
              getOptionLabel={(o) => o?.nombre ?? ""}
              isOptionEqualToValue={(o, v) => o.evaluadorId === v.evaluadorId}
              loading={cargandoEvaluadores}
              disabled={guardando}
              noOptionsText="No hay evaluadores para esta solicitud"
              loadingText="Cargando evaluadores…"
              renderOption={(props, option) => {
                const { key, ...resto } = props;
                return (
                  <li key={option.evaluadorId} {...resto}>
                    {option.nombre}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Evaluador"
                  placeholder="Seleccione el evaluador"
                />
              )}
            />

            <Button
              variant="contained"
              onClick={guardar}
              disabled={!actividad || !evaluador || guardando}
              sx={{ minWidth: 170, height: 56 }}
            >
              {guardando ? (
                <CircularProgress size={22} color="inherit" />
              ) : (
                "Asignar tarea"
              )}
            </Button>
          </Box>
        </div>
      </div>
    </div>
  );
}

export default ReconocimientoNC;
